// ============================================================
// CLUBHUB – Musterbestand fuer die Masken (SYNTHETISCH)
// ============================================================
// Kein einziger Name, keine Adresse und keine Nummer hier gehoert
// einem echten Menschen. Alles entsteht deterministisch aus einem
// festen Startwert — jede Maske sieht bei jedem Aufruf dieselben
// Menschen, und Rechnungen, Termine und Spenden anderer Module
// koennen sich auf dieselben IDs beziehen (`MOCK_CONTACTS`).
//
// Echt ist nur der Vereinsname (ein eingetragener Verein, oeffentlich).
// Die echten Daten kommen ausschliesslich ueber die Migration in die
// Datenbank — nie in dieses Repo.
// ============================================================
import type {
  ClubRole,
  ContactStatus,
  FeeInterval,
  GroupKind,
  PaymentMethod,
  Salutation,
} from "@/generated/prisma/enums";
import type {
  ClubView,
  ContactView,
  CurrentContext,
  FeeTypeView,
  GroupView,
  HouseholdView,
  UserView,
} from "@/lib/views";

// ── Zufall mit festem Startwert ──────────────────────────────
export function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function pick<T>(r: () => number, list: readonly T[]): T {
  return list[Math.floor(r() * list.length)]!;
}

export function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Stabile, lesbare IDs: "c-0007", "g-03" … */
export const cid = (n: number) => `c-${String(n).padStart(4, "0")}`;

const VORNAMEN_M = ["Max", "Jonas", "Lukas", "Felix", "Tobias", "Michael", "Stefan", "Florian", "Andreas", "Sebastian", "Thomas", "Markus", "Benedikt", "Korbinian", "Quirin", "Vitus", "Leon", "Paul", "Elias", "Matthias", "Simon", "Johannes", "Kilian", "Valentin"];
const VORNAMEN_W = ["Lena", "Anna", "Sophie", "Laura", "Julia", "Katharina", "Theresa", "Marie", "Franziska", "Johanna", "Magdalena", "Veronika", "Lisa", "Hannah", "Clara", "Sabine", "Monika", "Eva", "Leonie", "Emilia"];
const NACHNAMEN = ["Huber", "Wagner", "Bauer", "Mayer", "Gruber", "Hofer", "Berger", "Moser", "Brandl", "Steiner", "Fischer", "Weber", "Schneider", "Lechner", "Eder", "Winkler", "Kellner", "Reiter", "Haas", "Hartl", "Pichler", "Sommer", "Walter", "Kraus", "Lang", "Fuchs", "Frank", "Graf", "Riedl", "Seidl"];
const STRASSEN = ["Grünwalder Str.", "Tegernseer Landstr.", "Sankt-Martin-Str.", "Untersbergstr.", "Säbener Str.", "Candidstr.", "Pilgersheimer Str.", "Ichostr.", "Watzmannstr.", "Hochstr.", "Kolumbusstr.", "Silberhornstr."];
const ORTE: [string, string][] = [["81547", "München"], ["81541", "München"], ["81543", "München"], ["81539", "München"], ["82049", "Pullach"], ["82031", "Grünwald"], ["85521", "Ottobrunn"], ["82008", "Unterhaching"]];

// ── Verein, Person, Zugaenge ─────────────────────────────────
export const MOCK_CLUB: ClubView = {
  id: "club-ga1860",
  name: "Grünspitz Allstars 1860 e.V.",
  shortName: "Grünspitz Allstars",
  slug: "gruenspitz-allstars",
  initials: "GA",
  city: "München",
  memberCount: 0, // unten gesetzt
};

export const MOCK_CLUBS: ClubView[] = [
  MOCK_CLUB,
  { id: "club-demo", name: "TSV Musterdorf 1920 e.V.", shortName: "TSV Musterdorf", slug: "tsv-musterdorf", initials: "TM", city: "Musterdorf", memberCount: 412 },
];

export const MOCK_USER: UserView = {
  id: "u-0001",
  firstName: "Alex",
  lastName: "Brandl",
  email: "vorstand@example.org",
  color: null,
};

export const MOCK_ROLE: ClubRole = "BOARD";

// ── Gruppen ──────────────────────────────────────────────────
const GRUPPEN: { name: string; kind: GroupKind; parent?: string; alias?: string; desc?: string }[] = [
  { name: "Vorstand", kind: "BOARD", alias: "vorstand", desc: "Geschäftsführender Vorstand nach § 26 BGB" },
  { name: "Erweiterter Vorstand", kind: "BOARD", parent: "Vorstand" },
  { name: "1. Mannschaft", kind: "TEAM", alias: "erste" },
  { name: "Altherren", kind: "TEAM", alias: "ah" },
  { name: "Jugend", kind: "DEPARTMENT" },
  { name: "U13", kind: "TEAM", parent: "Jugend" },
  { name: "U17", kind: "TEAM", parent: "Jugend" },
  { name: "Fan-Stammtisch", kind: "FAN" },
  { name: "Festausschuss", kind: "COMMITTEE", desc: "Sommerfest, Weihnachtsfeier, Turniere" },
  { name: "Sponsoren", kind: "SPONSOR" },
];

export const MOCK_GROUPS: GroupView[] = GRUPPEN.map((g, i) => ({
  id: `g-${String(i + 1).padStart(2, "0")}`,
  name: g.name,
  kind: g.kind,
  parentId: null,
  description: g.desc ?? null,
  color: null,
  emailAlias: g.alias ? `${g.alias}@gruenspitz-allstars.example.org` : null,
  memberCount: 0,
}));
for (const [i, g] of GRUPPEN.entries()) {
  if (g.parent) MOCK_GROUPS[i]!.parentId = MOCK_GROUPS.find((x) => x.name === g.parent)!.id;
}
const gruppe = (name: string) => MOCK_GROUPS.find((g) => g.name === name)!;

// ── Beitragsarten ────────────────────────────────────────────
const BEITRAEGE: [string, number, FeeInterval, number | null, number | null, string | null][] = [
  ["Aktive Mitglieder", 72, "YEARLY", 18, null, "Spielbetrieb inkl. Versicherung"],
  ["Passive Mitglieder", 36, "YEARLY", 18, null, null],
  ["Jugend (bis 17)", 24, "YEARLY", null, 17, "Ermäßigt, Eltern zahlen"],
  ["Familie", 108, "YEARLY", null, null, "Alle Personen eines Haushalts"],
  ["Fördermitglied", 5, "MONTHLY", null, null, "Frei wählbar ab 5 € im Monat"],
  ["Ehrenmitglied", 0, "YEARLY", null, null, "Beitragsfrei"],
];

export const MOCK_FEE_TYPES: FeeTypeView[] = BEITRAEGE.map(([name, amount, interval, minAge, maxAge, desc], i) => ({
  id: `f-${i + 1}`,
  name,
  amount,
  interval,
  dueMonth: interval === "YEARLY" ? 3 : null,
  minAge,
  maxAge,
  isActive: true,
  description: desc,
}));

// ── Mitglieder und Kontakte ──────────────────────────────────
const STATUS_GEWICHTE: [ContactStatus, number][] = [
  ["ACTIVE", 0.62],
  ["PASSIVE", 0.16],
  ["HONORARY", 0.04],
  ["PROSPECT", 0.04],
  ["FORMER", 0.08],
  ["CONTACT", 0.06],
];

function status(r: () => number): ContactStatus {
  let x = r();
  for (const [s, w] of STATUS_GEWICHTE) {
    if ((x -= w) <= 0) return s;
  }
  return "ACTIVE";
}

const FUNKTIONEN_VORSTAND = ["1. Vorsitzende", "2. Vorsitzender", "Kassenwart", "Schriftführerin"];

function baueKontakte(): { contacts: ContactView[]; households: HouseholdView[] } {
  const r = rng(1860);
  const contacts: ContactView[] = [];
  const households: HouseholdView[] = [];
  let nr = 1;

  for (let i = 0; i < 96; i++) {
    const weiblich = r() < 0.42;
    const firstName = pick(r, weiblich ? VORNAMEN_W : VORNAMEN_M);
    const lastName = pick(r, NACHNAMEN);
    const jung = r() < 0.18;
    const jahr = jung ? 2009 + Math.floor(r() * 8) : 1952 + Math.floor(r() * 50);
    const [zip, city] = pick(r, ORTE);
    const s = status(r);
    const eintritt = 1990 + Math.floor(r() * 36);
    const fee: string | null =
      s === "CONTACT" || s === "PROSPECT"
        ? null
        : s === "HONORARY"
          ? "f-6"
          : jung
            ? "f-3"
            : s === "PASSIVE"
              ? "f-2"
              : "f-1";
    const pm: PaymentMethod = r() < 0.7 ? "SEPA" : "TRANSFER";
    const salutation: Salutation = weiblich ? "FRAU" : "HERR";
    const id = cid(i + 1);
    const memberNumber = s === "CONTACT" || s === "PROSPECT" ? null : String(nr++).padStart(4, "0");

    contacts.push({
      id,
      kind: "PERSON",
      memberNumber,
      salutation,
      title: r() < 0.04 ? "Dr." : null,
      firstName,
      lastName,
      organizationName: null,
      displayName: `${lastName}, ${firstName}`,
      birthDate: isoDate(jahr, 1 + Math.floor(r() * 12), 1 + Math.floor(r() * 28)),
      gender: weiblich ? "FEMALE" : "MALE",
      street: `${pick(r, STRASSEN)} ${1 + Math.floor(r() * 120)}`,
      zip,
      city,
      country: "DE",
      email: r() < 0.9 ? `${firstName}.${lastName}`.toLowerCase().replace(/[äöüß]/g, (c) => ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" })[c]!) + `${i}@example.org` : null,
      phone: r() < 0.3 ? `089 ${100000 + Math.floor(r() * 899999)}` : null,
      mobile: r() < 0.8 ? `0151 ${10000000 + Math.floor(r() * 89999999)}` : null,
      status: s,
      entryDate: s === "CONTACT" ? null : isoDate(eintritt, 1 + Math.floor(r() * 12), 1),
      exitDate: s === "FORMER" ? isoDate(2020 + Math.floor(r() * 6), 12, 31) : null,
      householdId: null,
      communication: r() < 0.9 ? "EMAIL" : "POST",
      consentNewsletter: r() < 0.7,
      consentPhotos: r() < 0.6,
      consentInternet: r() < 0.73,
      groups: [],
      feeTypeId: fee,
      paymentMethod: pm,
      hasMandate: pm === "SEPA",
      openAmount: s === "ACTIVE" && r() < 0.12 ? 72 : 0,
      notes: null,
      customFields: {
        trikotgroesse: pick(r, ["S", "M", "L", "XL", "XXL"]),
        dauerkarte: r() < 0.35,
      },
      createdAt: `${eintritt}-01-01T10:00:00`,
    });
  }

  // Sponsoren als Organisationen
  for (const [j, name] of ["Brauerei Muster GmbH", "Autohaus Beispiel KG", "Bäckerei Sonnengold"].entries()) {
    contacts.push({
      ...contacts[0]!,
      id: cid(200 + j),
      kind: "ORGANIZATION",
      memberNumber: null,
      salutation: "FIRMA",
      title: null,
      firstName: null,
      lastName: null,
      organizationName: name,
      displayName: name,
      birthDate: null,
      gender: null,
      consentInternet: true,
      status: "CONTACT",
      entryDate: null,
      exitDate: null,
      groups: [],
      feeTypeId: null,
      paymentMethod: "TRANSFER",
      hasMandate: false,
      openAmount: 0,
      email: `info@${name.split(" ")[1]!.toLowerCase()}.example.org`,
      customFields: {},
    });
  }

  // Familien: je zwei bis vier mit gleichem Nachnamen
  const nachName = new Map<string, ContactView[]>();
  for (const c of contacts) {
    if (c.kind !== "PERSON" || !c.lastName) continue;
    nachName.set(c.lastName, [...(nachName.get(c.lastName) ?? []), c]);
  }
  let h = 1;
  for (const [name, leute] of nachName) {
    if (leute.length < 2 || h > 6) continue;
    const id = `h-${h++}`;
    const mitglieder = leute.slice(0, 4);
    for (const m of mitglieder) {
      m.householdId = id;
      m.feeTypeId = m.feeTypeId && m.status !== "HONORARY" ? "f-4" : m.feeTypeId;
      m.street = mitglieder[0]!.street;
      m.zip = mitglieder[0]!.zip;
      m.city = mitglieder[0]!.city;
    }
    households.push({ id, name: `Familie ${name}`, contactIds: mitglieder.map((m) => m.id), billingContactId: mitglieder[0]!.id });
  }

  // Gruppen zuordnen
  const aktive = contacts.filter((c) => c.kind === "PERSON" && (c.status === "ACTIVE" || c.status === "HONORARY"));
  const dazu = (c: ContactView, g: GroupView, fn: string | null = null) => {
    if (c.groups.some((x) => x.groupId === g.id)) return;
    c.groups.push({ groupId: g.id, groupName: g.name, function: fn, since: c.entryDate });
  };
  aktive.slice(0, 4).forEach((c, k) => dazu(c, gruppe("Vorstand"), FUNKTIONEN_VORSTAND[k]!));
  aktive.slice(4, 9).forEach((c) => dazu(c, gruppe("Erweiterter Vorstand"), "Beisitz"));
  for (const c of aktive) {
    const alter = c.birthDate ? 2026 - Number(c.birthDate.slice(0, 4)) : 30;
    if (alter <= 13) dazu(c, gruppe("U13"));
    else if (alter <= 17) dazu(c, gruppe("U17"));
    else if (alter <= 34 && r() < 0.6) dazu(c, gruppe("1. Mannschaft"), r() < 0.06 ? "Kapitän" : null);
    else if (alter >= 35 && r() < 0.45) dazu(c, gruppe("Altherren"));
    if (r() < 0.3) dazu(c, gruppe("Fan-Stammtisch"));
    if (r() < 0.08) dazu(c, gruppe("Festausschuss"));
  }
  const trainer = aktive.find((c) => c.groups.some((g) => g.groupName === "1. Mannschaft"));
  if (trainer) trainer.groups.find((g) => g.groupName === "1. Mannschaft")!.function = "Trainer";
  for (const c of contacts.filter((c) => c.kind === "ORGANIZATION")) dazu(c, gruppe("Sponsoren"));

  return { contacts, households };
}

const gebaut = baueKontakte();
export const MOCK_CONTACTS: ContactView[] = gebaut.contacts;
export const MOCK_HOUSEHOLDS: HouseholdView[] = gebaut.households;

for (const g of MOCK_GROUPS) {
  g.memberCount = MOCK_CONTACTS.filter((c) => c.groups.some((x) => x.groupId === g.id)).length;
}
MOCK_CLUB.memberCount = MOCK_CONTACTS.filter((c) => ["ACTIVE", "PASSIVE", "HONORARY"].includes(c.status)).length;

export function mockContact(id: string): ContactView | undefined {
  return MOCK_CONTACTS.find((c) => c.id === id);
}

export function mockGroup(id: string): GroupView | undefined {
  return MOCK_GROUPS.find((g) => g.id === id);
}

export function mockFeeType(id: string | null): FeeTypeView | undefined {
  return id ? MOCK_FEE_TYPES.find((f) => f.id === id) : undefined;
}

export const MOCK_CONTEXT: CurrentContext = {
  user: MOCK_USER,
  club: MOCK_CLUB,
  clubs: MOCK_CLUBS,
  role: MOCK_ROLE,
  quelle: "muster",
};
